import React, { useEffect, useState } from 'react';
import api from '../../lib/api';

/**
 * RegimeGrid — compact top-strip on the Dashboard/Command tab that
 * shows the current market regime for each watchlist symbol as a
 * colour-coded pill. Regimes are fetched in parallel and cached
 * client-side for 60 s so tab-switches don't refetch.
 *
 * Props:
 *   symbols    — string[] — watchlist symbols
 *   onSelect   — fn(sym) — click handler (defaults to no-op)
 */
const REGIME_COLOURS = {
  trending_up:   { bg: 'bg-buy/10',       border: 'border-buy',     text: 'text-buy',       label: 'TREND ↑' },
  trending_down: { bg: 'bg-sell/10',      border: 'border-sell',    text: 'text-sell',      label: 'TREND ↓' },
  range:         { bg: 'bg-hold/10',      border: 'border-hold',    text: 'text-hold',      label: 'RANGE'   },
  high_vol:      { bg: 'bg-pink-500/10',  border: 'border-pink-400',text: 'text-pink-400',  label: 'HIGH V'  },
  low_vol:       { bg: 'bg-cyan-sys/10',  border: 'border-cyan-sys',text: 'text-cyan-sys',  label: 'LOW V'   },
  transition:    { bg: 'bg-white/5',      border: 'border-white/30',text: 'text-white/70',  label: 'TRANS'   },
  unknown:       { bg: 'bg-white/5',      border: 'border-white/20',text: 'text-white/50',  label: '—'       },
};

// Module-level cache: symbol → { ts, regime, confidence }
const _cache = new Map();
const CACHE_TTL_MS = 60_000;

export default function RegimeGrid({ symbols = [], onSelect }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async (sym) => {
      const now = Date.now();
      const hit = _cache.get(sym);
      if (hit && now - hit.ts < CACHE_TTL_MS) return { symbol: sym, ...hit };
      try {
        // Use 1h TF for regime — enough resolution without over-loading Claude.
        const { data } = await api.get(`/crypto/regime/${sym}`, { params: { tf: '1h' } });
        const summary = data?.summary || {};
        const row = {
          symbol: sym,
          ts: now,
          regime: summary.regime || 'unknown',
          confidence: Number(summary.confidence || 0),
        };
        _cache.set(sym, row);
        return row;
      } catch {
        return { symbol: sym, ts: now, regime: 'unknown', confidence: 0 };
      }
    };
    (async () => {
      if (!symbols.length) { setRows([]); return; }
      // Fetch in parallel — the /crypto/regime endpoint is deterministic
      // (no LLM call) so we can afford to hit it for every symbol.
      const results = await Promise.all(symbols.map(fetchOne));
      if (!cancelled) setRows(results);
    })();
    return () => { cancelled = true; };
  }, [symbols]);

  if (!symbols.length) return null;
  return (
    <div
      data-testid="dashboard-regime-grid"
      className="panel px-3 py-1.5 flex items-center gap-3 overflow-x-auto"
    >
      <span className="font-display tracking-[0.3em] uppercase text-[10px] text-cyan-sys shrink-0">// REGIME</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {rows.map((r) => {
          const col = REGIME_COLOURS[r.regime] || REGIME_COLOURS.unknown;
          return (
            <button
              key={r.symbol}
              data-testid={`dashboard-regime-${r.symbol}`}
              onClick={() => onSelect?.(r.symbol)}
              title={`${r.symbol} · ${r.regime.replace('_', ' ')} · ${r.confidence}% confidence`}
              className={`flex items-center gap-1.5 px-2 py-0.5 border ${col.border} ${col.bg} ${col.text} font-mono text-[10px] tracking-widest uppercase hover:brightness-125`}
            >
              <span className="text-[var(--txt-primary)] font-display">{r.symbol}</span>
              <span>{col.label}</span>
              <span className="tabular-nums opacity-70">{r.confidence}%</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
